import { Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  applyDiscount,
  BUYABLE_UPGRADE_TIERS,
  MAX_BUYABLE_TIER,
  pickUpgradeTier,
} from './upgradeTier';
import { IndexPath } from '@/Config/app/router/index.path';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { SubscriptionIntervalEnum } from '@/Domain/enum/SubscriptionInterval.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useGetSubscriptionPlans } from '@/Domain/hooks/Subscription/useGetSubscriptionPlans.hook';
import { useUpdateSubscription } from '@/Domain/hooks/Subscription/useUpdateSubscription.hook';

interface SiteScrapeUpgradeProps {
  selectedCount: number;
  // Pages the plan is sized for: the selection when it's over the limit, else the whole map.
  targetCount: number;
  selectionFits: boolean;
  usedCount: number;
  currentLimit: number | null;
  planLimit: number | null;
  // Paying subscribers change plans on the subscription page (ChangePlanDialog
  // with proration preview) — never via an unconfirmed in-place update from here.
  isPaidPlan: boolean;
  // Runs before upgrade_clicked is captured, so earlier review events stay ahead of it.
  onBeforeUpgradeClick?: () => void;
  onBeforeCheckout: () => Promise<void> | void;
  onManagePlan: () => Promise<void> | void;
  discountApplied: boolean;
  discountPercentOff: number;
  promoCode?: string;
  // Compact single-bar layout for the height-constrained review modal, so the page
  // list stays visible on short viewports instead of being pushed off-screen.
  dense?: boolean;
}

/**
 * Over-limit upsell: picks the single cheapest plan whose article capacity covers
 * the existing articles plus the target pages and presents just that one as
 * a compact, focused CTA.
 */
export const SiteScrapeUpgrade = ({
  selectedCount,
  targetCount,
  selectionFits,
  usedCount,
  currentLimit,
  planLimit,
  isPaidPlan,
  onBeforeUpgradeClick,
  onBeforeCheckout,
  onManagePlan,
  discountApplied,
  discountPercentOff,
  promoCode,
  dense = false,
}: SiteScrapeUpgradeProps) => {
  const { t, i18n } = useTranslation(['knowledge_base', 'getting_started', 'common']);
  const { data: plans } = useGetSubscriptionPlans();
  const [pending, setPending] = useState(false);
  const { capture } = usePostHogAnalytics();
  const navigate = useNavigate();

  const exactTier = pickUpgradeTier(BUYABLE_UPGRADE_TIERS, usedCount + targetCount, currentLimit);
  // Target exceeds every buyable plan but the user can still upgrade: pitch Business as a partial fit.
  const canPitchBusiness = MAX_BUYABLE_TIER.articles > (currentLimit ?? 0);
  const isPartial = !exactTier && canPitchBusiness;
  const tier = exactTier ?? (canPitchBusiness ? MAX_BUYABLE_TIER : null);
  // Existing articles already consume part of the tier, so only this many of the selection actually fit.
  const partialFit = Math.max(0, MAX_BUYABLE_TIER.articles - usedCount);
  const showDeselectHint = !selectionFits && planLimit !== null && planLimit > 0;
  const ctaVariant = selectionFits ? 'secondary' : 'dark';

  // Fires once per mount when the card appears; is_partial marks the Business-partial pitch.
  const impressionFiredRef = useRef(false);
  useEffect(() => {
    if (impressionFiredRef.current) return;
    impressionFiredRef.current = true;
    capture(PostHogEventEnum.SiteScrapeUpgradeShown, {
      selected_count: selectedCount,
      target_count: targetCount,
      selection_fits: selectionFits,
      plan_limit: planLimit,
      to_tier: tier?.id ?? null,
      is_partial: isPartial,
      discount_applied: discountApplied && !isPaidPlan,
    });
  }, [
    capture,
    selectedCount,
    targetCount,
    selectionFits,
    planLimit,
    tier,
    isPartial,
    discountApplied,
    isPaidPlan,
  ]);

  const stripePriceId = tier ? plans?.products[tier.productKey]?.price_monthly : undefined;
  const displayPrice = tier?.displayPrice ?? '';
  const discountedPrice =
    tier && discountApplied && !isPaidPlan ? applyDiscount(displayPrice, discountPercentOff) : null;
  const planName = tier ? t(`getting_started:pricing_step.tiers.${tier.key}.name`) : '';

  const updateSubscription = useUpdateSubscription({
    successCallback: () => setPending(false),
    errorCallback: () => {
      setPending(false);
      toast.error(t('getting_started:pricing_step.checkout_error_toast'));
    },
    fromTier: TierEnum.FREE,
    toTier: tier?.id ?? TierEnum.FREE,
    interval: SubscriptionIntervalEnum.Monthly,
    source: 'site_scrape_review',
    promotionCode: discountApplied && !isPaidPlan ? promoCode : undefined,
  });

  const handleUpgrade = async () => {
    if (!tier || pending) return;
    onBeforeUpgradeClick?.();
    capture(PostHogEventEnum.SiteScrapeUpgradeClicked, {
      selected_count: selectedCount,
      target_count: targetCount,
      selection_fits: selectionFits,
      plan_limit: planLimit,
      to_tier: tier.id,
      method: isPaidPlan ? 'manage_plan' : 'checkout',
    });
    if (isPaidPlan) {
      await onManagePlan();
      navigate(IndexPath.subscription);
      return;
    }
    if (!stripePriceId) return;
    setPending(true);
    await onBeforeCheckout();
    void updateSubscription.handleUpdate(stripePriceId);
  };

  const handleEnterprise = () => {
    onBeforeUpgradeClick?.();
    capture(PostHogEventEnum.SiteScrapeUpgradeClicked, {
      selected_count: selectedCount,
      target_count: targetCount,
      selection_fits: selectionFits,
      plan_limit: planLimit,
      to_tier: TierEnum.CUSTOM,
      method: 'enterprise',
    });
    // Match the Subscription page's Enterprise CTA: open contact in a new tab, leaving the review open.
    window.open('https://www.quickchat.ai/contact', '_blank', 'noopener,noreferrer');
  };

  const enterpriseRow = (
    <button
      type="button"
      onClick={handleEnterprise}
      className="cursor-pointer text-center text-xs font-medium text-info hover:underline"
      data-testid="site-scrape-review-enterprise-cta"
    >
      {t('tabs.website.review.enterprise_cta', { pages: targetCount })}
    </button>
  );

  // Already on the top self-serve plan: point the overflow at Enterprise, keep deselect.
  if (!tier) {
    return (
      <div
        className="flex shrink-0 flex-col gap-2 rounded-xl border border-border bg-muted/60 p-3"
        data-testid="site-scrape-review-upgrade-none"
      >
        {enterpriseRow}
        {showDeselectHint && (
          <p className="text-center text-xs text-muted-foreground">
            {t('tabs.website.review.smart_upgrade_deselect', { limit: planLimit })}
          </p>
        )}
      </div>
    );
  }

  // Compact one-bar variant for the height-constrained review modal: keeps the
  // upsell present but small so the page list stays the focus on short viewports.
  if (dense) {
    return (
      <div
        className="flex shrink-0 flex-col gap-2 rounded-xl border border-info/20 bg-gradient-to-b from-info-muted/40 to-card p-3 shadow-sm"
        data-testid="site-scrape-review-upgrade"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-col">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <Sparkles className="size-3.5 shrink-0 text-info" />
              <span className="truncate">
                {isPartial
                  ? t('tabs.website.review.partial_upgrade_title', {
                      fits: partialFit,
                      count: targetCount,
                    })
                  : t('tabs.website.review.smart_upgrade_title', { count: targetCount })}
              </span>
            </span>
            <span className="truncate pl-5 text-xs text-muted-foreground">
              {isPartial ? (
                t('tabs.website.review.partial_upgrade_subtitle', { fits: partialFit })
              ) : (
                <>
                  {planName} · {discountedPrice ?? displayPrice}
                  {t('getting_started:pricing_step.per_month')}
                </>
              )}
            </span>
          </div>
          <Button
            variant={ctaVariant}
            size="sm"
            onClick={() => void handleUpgrade()}
            disabled={pending || (!isPaidPlan && !stripePriceId)}
            className="shrink-0"
            data-testid="site-scrape-review-upgrade-cta"
          >
            {pending ? (
              <Spinner className="size-4" />
            ) : isPaidPlan ? (
              t('common:upgrade.action')
            ) : (
              t('tabs.website.review.smart_upgrade_cta', { plan: planName })
            )}
          </Button>
        </div>
        {isPartial && enterpriseRow}
        {showDeselectHint && (
          <p className="text-xs text-muted-foreground">
            {t('tabs.website.review.smart_upgrade_deselect', { limit: planLimit })}
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex shrink-0 flex-col gap-2.5 rounded-xl border border-info/20 bg-gradient-to-b from-info-muted/40 to-card p-3 shadow-sm"
      data-testid="site-scrape-review-upgrade"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-info-muted px-2 py-0.5 text-xs font-medium text-info">
          <Sparkles className="size-3" />
          {t('tabs.website.review.smart_upgrade_badge')}
        </span>
        <div className="flex items-baseline gap-1.5">
          {discountedPrice && (
            <span className="text-xs font-medium text-muted-foreground line-through">
              {displayPrice}
            </span>
          )}
          <span className="text-xl font-semibold tracking-tight text-foreground">
            {discountedPrice ?? displayPrice}
          </span>
          <span className="text-xs text-muted-foreground">
            {t('getting_started:pricing_step.per_month')}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold tracking-tight text-foreground">
          {isPartial
            ? t('tabs.website.review.partial_upgrade_title', {
                fits: partialFit,
                count: targetCount,
              })
            : t('tabs.website.review.smart_upgrade_title', { count: targetCount })}
        </p>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {isPartial
            ? t('tabs.website.review.partial_upgrade_subtitle', { fits: partialFit })
            : t('tabs.website.review.smart_upgrade_subtitle', {
                plan: planName,
                articles: tier.articles.toLocaleString(i18n.language),
              })}
        </p>
      </div>

      <Button
        variant={ctaVariant}
        size="sm"
        onClick={() => void handleUpgrade()}
        disabled={pending || (!isPaidPlan && !stripePriceId)}
        className="w-full"
        data-testid="site-scrape-review-upgrade-cta"
      >
        {pending ? (
          <Spinner className="size-4" />
        ) : isPaidPlan ? (
          t('common:upgrade.action')
        ) : (
          t('tabs.website.review.smart_upgrade_cta', { plan: planName })
        )}
      </Button>

      {isPartial && enterpriseRow}

      {showDeselectHint && (
        <p className="text-center text-xs text-muted-foreground">
          {t('tabs.website.review.smart_upgrade_deselect', { limit: planLimit })}
        </p>
      )}
    </div>
  );
};
